import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Edit, Delete } from "@mui/icons-material";
import axios from "axios";
import BASE_URL from "../config"; // Replace with your API base URL

const AcademicYearPage = () => {
  const [academicYears, setAcademicYears] = useState([]);
  const [courses, setCourses] = useState([]);
  const [academicYearName, setAcademicYearName] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [type, setType] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("");
  const [editId, setEditId] = useState(null);
  const storedName = sessionStorage.getItem('adminName');
  const [loggedInUser, setLoggedInUser] = useState("storedName");

  useEffect(() => {
    fetchCourses();
    fetchAcademicYears();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/course`);
      setCourses(response.data);
    } catch (error) {
      console.error("Error fetching courses:", error);
    }
  };

  const fetchAcademicYears = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/academic-years`);
      setAcademicYears(response.data);
    } catch (error) {
      console.error("Error fetching academic years:", error);
    }
  };

  const handleAddOrUpdate = async () => {
    if (!academicYearName || !startDate || !endDate || !type || !selectedCourse) {
      alert("Please fill all fields.");
      return;
    }

    const payload = {
      AcademicYearName: academicYearName,
      StartDate: dayjs(startDate).format("YYYY-MM-DD"), // Format start date
      EndDate: dayjs(endDate).format("YYYY-MM-DD"),     // Format end date
      Type: type,
      CourseID: selectedCourse,
      CreatedBy: loggedInUser,
      CreatedDate: dayjs().format("YYYY-MM-DD"),       // Use current date
    };

    try {
      if (editId) {
        await axios.put(`${BASE_URL}/api/academic-years/${editId}`, payload);
        alert("Academic Year updated successfully.");
      } else {
        await axios.post(`${BASE_URL}/api/academic-years`, payload);
        alert("Academic Year added successfully.");
      }
      resetForm();
      fetchAcademicYears();
    } catch (error) {
      if (error.response && error.response.status === 400) {
        alert(error.response.data);
      } else {
        console.error("Error adding/updating academic year:", error);
      }
    }
  };

  const handleEdit = (academicYear) => {
    setAcademicYearName(academicYear.AcademicYearName);
    setStartDate(academicYear.StartDate ? dayjs(academicYear.StartDate) : null);
    setEndDate(academicYear.EndDate ? dayjs(academicYear.EndDate) : null);
    setType(academicYear.Type);
    setSelectedCourse(academicYear.CourseID);
    setEditId(academicYear.AcademicYearID);
    /* window.scrollTo({ top: 0, behavior: "smooth" }); */
    const container = document.querySelector('#root');
   container.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {


    if (!window.confirm("Are you sure you want to delete this Year?")) {
    return;
  }

    try {
       const res = await axios.delete(`${BASE_URL}/api/academic-years/${id}`);
      //alert("Academic Year deleted successfully.");
      alert(res.data.message || "Academic Year deleted successfully.");
      fetchAcademicYears();
    } catch (error) {
      console.error("Error deleting AcademicYear:", error);

    alert(
      error.response?.data?.message ||
      "Unable to delete Academic Year."
    );
      //console.error("Error deleting academic year:", error);
    }
  };

  const resetForm = () => {
    setAcademicYearName("");
    setStartDate(null);
    setEndDate(null);
    setType("");
    setSelectedCourse("");
    setEditId(null);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box p={3}>
        <Typography variant="h4" mb={2}>
          Manage Academic Years
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap" mb={3} alignItems="center">
          <TextField
            label="Academic Year Name"
            variant="outlined"
            value={academicYearName}
            onChange={(e) => setAcademicYearName(e.target.value)}
            fullWidth
          />
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(newValue) => setStartDate(newValue)}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(newValue) => setEndDate(newValue)}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
          <TextField
            label="Type"
            variant="outlined"
            select
            value={type}
            onChange={(e) => setType(e.target.value)}
            fullWidth
          >
            <MenuItem value="Semester">Semester</MenuItem>
            <MenuItem value="Yearly">Yearly</MenuItem>
          </TextField>
          <TextField
            label="Course"
            variant="outlined"
            select
            value={selectedCourse}
            onChange={(e) => setSelectedCourse(e.target.value)}
            fullWidth
          >
            {courses.map((course) => (
              <MenuItem key={course.CourseID} value={course.CourseID}>
                {course.CourseName}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" color="primary" onClick={handleAddOrUpdate}>
            {editId ? "Update Academic Year" : "Add Academic Year"}
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table
           sx={{
            border: "1px solid #ccc", // Add border to the table
            borderRadius: "8px", // Rounded corners
          }}
          >
            <TableHead
             sx={{
              backgroundColor: "black", // Dark blue background
              "& th": {
                color: "#fff", // White text color for header
                fontWeight: "bold", // Bold text
                fontSize: "16px", // Font size
              },
            }}
            >
              <TableRow>
                <TableCell>Academic Year Name</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Course</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {academicYears.map((year) => (
                <TableRow key={year.AcademicYearID}>
                  <TableCell>{year.AcademicYearName}</TableCell>
                 {/*  <TableCell>{year.StartDate}</TableCell> */}
                 <TableCell>{dayjs(year.StartDate).format("DD-MM-YYYY")}</TableCell>
                 {/*  <TableCell>{year.EndDate}</TableCell> */}
                 <TableCell>{dayjs(year.EndDate).format("DD-MM-YYYY")}</TableCell>
                  <TableCell>{year.Type}</TableCell>
                  <TableCell>{year.CourseName}</TableCell>
                  <TableCell>
                    <IconButton color="primary" onClick={() => handleEdit(year)}>
                      <Edit />
                    </IconButton>
                    <IconButton color="secondary" onClick={() => handleDelete(year.AcademicYearID)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </LocalizationProvider>
  );
};

export default AcademicYearPage;
